import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, FileText, Download, Archive, AlertTriangle, Lock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatKina } from "@/lib/money";
import { APPROVE } from "@/constants/testIds";

export default function PayRunApprove() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [period, setPeriod] = useState(null);
    const [lines, setLines] = useState([]);
    const [approving, setApproving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [typed, setTyped] = useState("");
    const [leaveAck, setLeaveAck] = useState(false);
    const [bankAck, setBankAck] = useState(false);

    // Leave gate fires ONLY when the run has AL or sick hours. No leave = no
    // gate — see backend approve endpoint. Keep it rare so it stays meaningful.
    const runHasLeave = useMemo(
        () => lines.some((ln) => {
            const h = ln.hours || {};
            return Number(h.annual_leave_taken || 0) > 0 || Number(h.sick || 0) > 0;
        }),
        [lines],
    );

    useEffect(() => {
        api.get(`/pay-periods/${id}`).then(({ data }) => {
            setPeriod(data.period);
            setLines(data.lines);
            // If already approved/locked, bounce to success screen — no double-approve.
            if (data.period.status !== "draft") {
                navigate(`/pay-runs/${id}/complete`, { replace: true });
            }
        });
    }, [id, navigate]);

    const missingBank = useMemo(() => {
        return lines
            .filter((ln) => ln.active !== false) // inactive rows won't be paid
            .filter((ln) => Number(ln.calc?.net_pay || 0) > 0)
            .filter((ln) => {
                const bank = (ln.employee?.bank) || {};
                return !((bank.bsb || "").trim() && (bank.account_number || "").trim());
            })
            .map((ln) => ln.employee?.name)
            .filter(Boolean);
    }, [lines]);

    const inactiveNames = useMemo(
        () => lines.filter((ln) => ln.active === false).map((ln) => ln.employee?.name).filter(Boolean),
        [lines],
    );

    const runHasMissingBank = missingBank.length > 0;

    const ppe = period?.ppe_date || "";
    const typedMatches = typed.trim().toLowerCase() === ppe.toLowerCase();

    const doApprove = async () => {
        if (!typedMatches) return;
        if (runHasLeave && !leaveAck) return;
        if (runHasMissingBank && !bankAck) return;
        setApproving(true);
        try {
            const body = {};
            if (runHasLeave) body.leave_entitlement_acknowledged = true;
            if (runHasMissingBank) body.bank_details_missing_acknowledged = true;
            await api.post(`/pay-periods/${id}/approve`, body);
            toast.success("Pay run approved and locked");
            navigate(`/pay-runs/${id}/complete`);
        } catch (ex) {
            // Aggregated gate envelope — pull the codes out and surface them
            // in the toast so the operator sees WHY approve was refused
            // (rather than a generic "Approve failed").
            const detail = ex?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "approve_gate_violations") {
                const blocks = detail.blocks || [];
                const acks = detail.acknowledgements_required || [];
                const parts = [];
                if (blocks.length) {
                    parts.push(`${blocks.length} unresolved: ` + blocks.map((b) => b.detail || b.name).join("; "));
                }
                if (acks.length) {
                    parts.push(`${acks.length} acknowledgement(s) required`);
                }
                toast.error(parts.join(" · ") || "Approve refused by gate");
            } else {
                toast.error(formatApiError(detail) || "Approve failed");
            }
            setApproving(false);
        }
    };

    if (!period) return <div className="p-10 text-neutral-500 text-sm">Loading…</div>;

    // Headcount reflects the workers who will actually be paid on this run;
    // inactive rows stay visible in the pay-run screen for audit but never
    // end up in the payslip zip, bank CSV or IRC remittance figures.
    const staff = lines.filter((ln) => ln.active !== false).length;
    const totals = period.totals || { gross: 0, tax: 0, net: 0 };

    return (
        <div data-testid={APPROVE.page} className="p-10 max-w-[1100px]">
            <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                Review &amp; approve · PPE {period.ppe_date}
            </div>
            <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                Ready to approve.
            </h1>

            {/* Hero */}
            <div className="mt-6 relative rounded-[3px] p-8" style={{ background: "var(--kp-black)", color: "#fff" }}>
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: "var(--kp-red)" }} />
                <div className="flex items-center gap-3">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "var(--kp-success)" }} />
                    <div className="font-display font-bold text-2xl">Ready to Approve</div>
                </div>
                <div className="text-neutral-400 text-sm mt-1">
                    {staff} staff · all hours reviewed · no outliers flagged
                </div>

                <div className="grid grid-cols-3 gap-8 mt-8">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Total gross</div>
                        <div data-testid={APPROVE.kpiGross} className="font-display font-bold text-4xl mt-1 font-mono-money">{formatKina(totals.gross)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Tax to IRC</div>
                        <div data-testid={APPROVE.kpiTax} className="font-display font-bold text-4xl mt-1 font-mono-money" style={{ color: "#FF6B75" }}>{formatKina(totals.tax)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Net to workers</div>
                        <div data-testid={APPROVE.kpiNet} className="font-display font-bold text-4xl mt-1 font-mono-money" style={{ color: "#59C783" }}>{formatKina(totals.net)}</div>
                    </div>
                </div>
            </div>

            {inactiveNames.length > 0 && (
                <div
                    data-testid="approve-inactive-warning"
                    className="mt-6 flex items-start gap-3 px-4 py-3 border-l-2 rounded-[3px]"
                    style={{ background: "#FEF6E6", borderLeftColor: "#C88A17", color: "#5A3600" }}
                >
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: "#C88A17" }} />
                    <div className="text-[13px] leading-relaxed">
                        <strong className="font-semibold">
                            {inactiveNames.length} inactive worker{inactiveNames.length === 1 ? "" : "s"} on this run:
                        </strong>{" "}
                        {inactiveNames.join(", ")}.<br />
                        They stay on the run for audit but won't be paid — no payslip PDF, no bank CSV line, and their money isn't in the totals above. Reactivate on the master before approving if this is wrong.
                    </div>
                </div>
            )}

            {/* Bank-details pre-flight warning.
                Now paired with a gate ack (see modal) — behaviour: these workers
                DO NOT ship in the bank CSV. Old copy told the operator the bank
                would reject empty rows; the app no longer emits empty rows at
                all, it excludes them and lists them on the Success screen. */}
            {runHasMissingBank && (
                <div
                    data-testid="approve-bank-warning"
                    className="mt-6 flex items-start gap-3 px-4 py-3 border-l-2 rounded-[3px]"
                    style={{ background: "var(--kp-warn-bg)", borderLeftColor: "var(--kp-warn)", color: "#5A3600" }}
                >
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: "var(--kp-warn)" }} />
                    <div className="text-[13px] leading-relaxed">
                        <strong className="font-semibold">Bank details missing for {missingBank.length} payee(s) with a positive net:</strong>{" "}
                        {missingBank.join(", ")}.<br />
                        These workers will be <strong>excluded</strong> from the bank payment CSV — the file will pay everyone else. Confirm they will be paid another way, or fix bank details on their profile before approving.
                    </div>
                </div>
            )}

            {/* What auto-generates */}
            <div className="mt-6 bg-white border border-neutral-200 rounded-[3px] p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-3">
                    On approve, we&rsquo;ll generate
                </div>
                <ul className="space-y-2 text-sm text-neutral-700">
                    <li className="flex items-center gap-2"><FileText size={14} className="text-neutral-400" /> Payslip PDF per worker (KP-branded)</li>
                    <li className="flex items-center gap-2"><Archive size={14} className="text-neutral-400" /> A single .zip of every payslip for the fortnight</li>
                    <li className="flex items-center gap-2"><Download size={14} className="text-neutral-400" /> Bank payment CSV — one line per worker with a positive net</li>
                    <li className="flex items-center gap-2"><FileText size={14} className="text-neutral-400" /> IRC remittance summary PDF (tax to withhold)</li>
                    <li className="flex items-center gap-2"><FileText size={14} className="text-neutral-400" /> Locked pay-run PDF for filing</li>
                </ul>
                <div className="text-[11px] text-neutral-400 mt-4">
                    Employee data (rate, deps, tax status, bank) is snapshotted into each pay line at this moment. Later master edits won&rsquo;t change this run.
                </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex items-center justify-between">
                <button
                    data-testid={APPROVE.backBtn}
                    onClick={() => navigate(`/pay-runs/${id}`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black"
                >
                    <ArrowLeft size={14} /> Back to pay run
                </button>
                <button
                    data-testid={APPROVE.approveBtn}
                    onClick={() => { setTyped(""); setConfirmOpen(true); }}
                    className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-display font-semibold uppercase tracking-[0.14em] text-white rounded-[3px]"
                    style={{ background: "var(--kp-red)" }}
                >
                    <Check size={16} /> Approve &amp; lock pay run
                </button>
            </div>

            {confirmOpen && (
                <ConfirmModal
                    ppe={ppe}
                    typed={typed}
                    setTyped={setTyped}
                    typedMatches={typedMatches}
                    approving={approving}
                    runHasLeave={runHasLeave}
                    leaveAck={leaveAck}
                    setLeaveAck={setLeaveAck}
                    runHasMissingBank={runHasMissingBank}
                    missingBankNames={missingBank}
                    bankAck={bankAck}
                    setBankAck={setBankAck}
                    onCancel={() => !approving && setConfirmOpen(false)}
                    onConfirm={doApprove}
                />
            )}
        </div>
    );
}

function ConfirmModal({
    ppe, typed, setTyped, typedMatches, approving,
    runHasLeave, leaveAck, setLeaveAck,
    runHasMissingBank, missingBankNames, bankAck, setBankAck,
    onCancel, onConfirm,
}) {
    const canSubmit =
        typedMatches &&
        (!runHasLeave || leaveAck) &&
        (!runHasMissingBank || bankAck);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
            <div
                data-testid={APPROVE.confirmModal}
                className="relative bg-white border border-neutral-200 shadow-2xl w-full max-w-[520px] rounded-[3px]"
            >
                <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-[2px]" style={{ background: "var(--kp-red)" }}>
                            <Lock size={16} className="text-white" />
                        </span>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                                Confirm approval
                            </div>
                            <div className="font-display font-bold text-xl text-[color:var(--kp-charcoal)] mt-0.5">
                                This locks the pay run
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid={APPROVE.confirmCancelBtn + "-x"}
                        onClick={onCancel}
                        disabled={approving}
                        className="p-1 text-neutral-400 hover:text-black"
                        aria-label="Cancel"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-[13px] leading-relaxed text-neutral-700">
                        Approving locks this pay period. You <strong>cannot</strong> edit hours, rates or add employees
                        after this. Every worker&rsquo;s rate, dependants, tax status and bank details are snapshotted onto
                        their pay line right now. <strong>This cannot be undone</strong> from within the app.
                    </p>
                    <div>
                        <label
                            htmlFor="approve-confirm-input"
                            className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1"
                        >
                            Type the pay-period-ending date to confirm
                        </label>
                        <div
                            data-testid={APPROVE.confirmHelper}
                            className="text-[12px] text-neutral-500 mb-2"
                        >
                            Type exactly: <code className="font-mono-money bg-neutral-100 px-1.5 py-0.5 rounded-[2px] text-[color:var(--kp-charcoal)]">{ppe}</code>
                        </div>
                        <input
                            id="approve-confirm-input"
                            data-testid={APPROVE.confirmInput}
                            type="text"
                            autoFocus
                            autoComplete="off"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="kp-focus w-full font-mono-money border border-neutral-300 px-3 py-2 text-[14px] bg-white rounded-[3px]"
                        />
                    </div>

                    {runHasLeave && (
                        <div
                            data-testid="approve-leave-ack-block"
                            className="mt-2 border-l-2 px-3 py-3 rounded-[3px]"
                            style={{ background: "#FEF6E6", borderLeftColor: "#C88A17", color: "#5A3600" }}
                        >
                            <div className="text-[12.5px] leading-relaxed">
                                This run pays and/or accrues leave under the accrual rates supplied
                                <strong> 4 September 2026</strong>. PNG-staff leave entitlement itself has
                                NOT been confirmed in writing — the director and bookkeeper disagree.
                                By approving you acknowledge this and take responsibility for the
                                leave amounts on this run.
                            </div>
                            <label className="mt-2 flex items-start gap-2 text-[12.5px] cursor-pointer select-none">
                                <input
                                    data-testid="approve-leave-ack-checkbox"
                                    type="checkbox"
                                    checked={leaveAck}
                                    onChange={(e) => setLeaveAck(e.target.checked)}
                                    className="mt-0.5 accent-[color:var(--kp-red)]"
                                />
                                <span>
                                    I acknowledge the leave-entitlement question is unresolved and
                                    approve this run under the current accrual rates anyway.
                                </span>
                            </label>
                        </div>
                    )}

                    {runHasMissingBank && (
                        <div
                            data-testid="approve-bank-ack-block"
                            className="mt-2 border-l-2 px-3 py-3 rounded-[3px]"
                            style={{ background: "var(--kp-warn-bg)", borderLeftColor: "var(--kp-warn)", color: "#5A3600" }}
                        >
                            <div className="text-[12.5px] leading-relaxed">
                                <strong>{missingBankNames.length} worker(s) will be EXCLUDED from the bank CSV</strong>{" "}
                                because they have no BSB or account number on file:
                                {" "}{missingBankNames.join(", ")}. Their pay row still exists on the run
                                and the payslip still generates — but no bank payment line will be produced
                                for them. Confirm they will be paid another way, or cancel and fix their
                                bank details first.
                            </div>
                            <label className="mt-2 flex items-start gap-2 text-[12.5px] cursor-pointer select-none">
                                <input
                                    data-testid="approve-bank-ack-checkbox"
                                    type="checkbox"
                                    checked={bankAck}
                                    onChange={(e) => setBankAck(e.target.checked)}
                                    className="mt-0.5 accent-[color:var(--kp-red)]"
                                />
                                <span>
                                    I acknowledge these workers will be omitted from the bank
                                    payment file and will be paid another way.
                                </span>
                            </label>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
                    <button
                        data-testid={APPROVE.confirmCancelBtn}
                        onClick={onCancel}
                        disabled={approving}
                        className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid={APPROVE.confirmSubmitBtn}
                        onClick={onConfirm}
                        disabled={!canSubmit || approving}
                        title={
                            !typedMatches ? `Type ${ppe} exactly to unlock`
                            : (runHasLeave && !leaveAck ? "Tick the leave-entitlement acknowledgement to unlock"
                            : (runHasMissingBank && !bankAck ? "Tick the missing-bank-details acknowledgement to unlock"
                            : "Approve & lock"))
                        }
                        className="inline-flex items-center gap-2 px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.14em] text-white rounded-[3px] disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: "var(--kp-red)" }}
                    >
                        <Lock size={14} /> {approving ? "Approving…" : "Approve & lock"}
                    </button>
                </div>
            </div>
        </div>
    );
}
